'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';

import styled from '@emotion/styled';
import { useTranslations } from 'next-intl';

import { BadgesHubTile } from '@web/features/hub-rework/BadgesHubTile';
import { BrandAssetsTile } from '@web/features/hub-rework/BrandAssetsTile';
// ChatTile + CloutChaseTile components are intentionally not imported here
// (the tiles were removed from the hub sidebar). The component files remain
// in features/hub-rework/{ChatTile,CloutChaseTile} for future reuse.
import { CollectionHubTile } from '@web/features/hub-rework/CollectionHubTile';
import { CouncilTile } from '@web/features/hub-rework/CouncilTile';
import { HanaAiAgentTile } from '@web/features/hub-rework/HanaAiAgentTile';
import { ImageTrail } from '@web/features/hub-rework/ImageTrail';
import {
  DARK_IMAGES,
  LIGHT_IMAGES,
  getRandomImages,
} from '@web/features/hub-rework/ImageTrail/constants';
import { ImagineTile } from '@web/features/hub-rework/ImagineTile';
import { LoreTile } from '@web/features/hub-rework/LoreTile';
import { MangaTile } from '@web/features/hub-rework/MangaTile';
import { PersonaJourneyTile } from '@web/features/hub-rework/PersonaJourneyTile';
import { PersonaProgramTile } from '@web/features/hub-rework/PersonaProgramTile';
import { RecordsTile } from '@web/features/hub-rework/RecordsTile';
import { StakingHubTile } from '@web/features/hub-rework/StakingHubTile';
import { TheRoomTile } from '@web/features/hub-rework/TheRoomTile';
import { TimeFallValleyTile } from '@web/features/hub-rework/TimeFallValleyTile';
import { TrailersTile } from '@web/features/hub-rework/TrailersTile';
import { UnaTokenChartTile } from '@web/features/hub-rework/UnaTokenChartTile';
import { UnaTokenTile } from '@web/features/hub-rework/UnaTokenTile';
import { UserHubTile } from '@web/features/hub-rework/UserHubTile';
import { useCurrentCompletedUser } from '@web/features/user/service';
import { Avatar } from '@web/ui/components';
import { NotConnectedIcon } from '@web/ui/icons';
import { useDesignsTokenTheme } from '@web/ui/theme/ThemeProvider';
import { transientOptions } from '@web/ui/theme/transientOptions';
import { getStateLayerColor } from '@web/ui/theme/utils';
import { env } from '@web/utils/env';

const HomeLayout = styled.main`
  position: relative;
  /* The image trail breaks out of this column to the full viewport width
     (see ImageTrailContainer); clip keeps that from adding a page scrollbar. */
  overflow-x: clip;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.color.background};
  padding-inline: ${({ theme }) => theme.spacing('0500')};
  padding-block: 4rem;
  gap: 4rem;
  ${({ theme }) => theme.breakpoints.up('sm')} {
    padding-block: 8rem;
    gap: 8rem;
    padding-inline: ${({ theme }) => theme.spacing('1000')};
    max-width: 100rem;
    margin-inline: auto;
    box-sizing: border-box;
    width: 100%;
  }
`;

const HeroMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-inline: ${({ theme }) => theme.spacing('2000')};
  gap: ${({ theme }) => theme.spacing('3000')};
`;

const HeroMessageTitle = styled.h1`
  text-align: center;
  white-space: pre;
  color: ${({ theme }) => theme.color.onBackground};
  font: ${({ theme }) => theme.typography.brand.bold};
  font-size: ${({ theme }) => theme.typescale.size['14']};
  line-height: ${({ theme }) => theme.typescale.lineHeight.condensed};
  letter-spacing: ${({ theme }) => theme.typescale.tracking.decreased};
  z-index: 1;
  pointer-events: none;

  ${({ theme }) => theme.breakpoints.down('lg')} {
    font-size: ${({ theme }) => theme.typescale.size['12']};
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    white-space: unset;
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    /* Scales with the viewport so "Persona Journey" stays on one line on a
       phone; the fixed step wrapped it below ~400px. */
    font-size: min(${({ theme }) => theme.typescale.size['10']}, 10.5vw);
    white-space: nowrap;
  }
`;

const HeroMessageSubtitle = styled.h2`
  z-index: 1;
  pointer-events: none;
  max-width: 40rem;
  text-align: center;
  text-wrap: balance;
  color: ${({ theme }) => theme.color.onBackground};
  font: ${({ theme }) => theme.typography.body.regular};
  font-size: ${({ theme }) => theme.typescale.size['5']};
  line-height: ${({ theme }) => theme.typescale.lineHeight.extended};
  opacity: ${({ theme }) => theme.opacity.dense};

  ${({ theme }) => theme.breakpoints.down('sm')} {
    font-size: ${({ theme }) => theme.typescale.size['2']};
  }
`;

const Container = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  align-self: stretch;
  gap: ${({ theme }) => theme.spacing('1000')};

  ${({ theme }) => theme.breakpoints.down('md')} {
    flex-direction: column;
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    gap: ${({ theme }) => theme.spacing('0500')};
  }
`;

const Sidebar = styled.div`
  display: flex;
  flex: 1 0 0;
  width: 100%;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing('1000')};

  ${({ theme }) => theme.breakpoints.up('md')} {
    max-width: 22.5rem;
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    gap: ${({ theme }) => theme.spacing('0500')};
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing('1000')};
  flex: 1 0 0;
  width: 100%;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    gap: ${({ theme }) => theme.spacing('0500')};
  }
`;

const DynamicContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  align-self: stretch;
`;

const TabsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  width: 100%;
  flex-shrink: 0;
  ${({ theme }) => theme.breakpoints.up('lg')} {
    max-width: 40rem;
  }
`;

const Tab = styled.div<{ active: boolean }>`
  height: ${({ theme }) => theme.size[4000]};
  box-sizing: border-box;
  cursor: pointer;
  display: flex;
  align-items: center;
  align-content: center;
  flex: 1 0 0;
  flex-wrap: wrap;
  border-top-left-radius: ${({ theme }) => theme.shape.corner['1000']};
  border-top-right-radius: ${({ theme }) => theme.shape.corner['1000']};
  padding: ${({ theme }) => theme.spacing('1500')};
  background: ${({ active, theme }) =>
    active ? theme.color.surfaceContainer : theme.color.surfaceContainerLowest};
  color: ${({ theme }) => theme.color.onSurface};
  &:hover {
    background-image: ${getStateLayerColor('onSurface', 'hover')};
  }

  :active {
    background-image: ${getStateLayerColor('onSurface', 'active')};
  }
`;

const TabLabel = styled.span`
  flex: 1;
  color: ${({ theme }) => theme.color.onSurface};
  font: ${({ theme }) => theme.typography.mono.regular};
  font-size: ${({ theme }) => theme.typescale.size['3']};
`;

const TabAvatar = styled(Avatar)`
  width: ${({ theme }) => theme.size[1500]};
  height: ${({ theme }) => theme.size[1500]};
  border: ${({ theme }) => `1px solid ${theme.color.onSurface}`};
`;

const EcoSystemTileContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  padding: ${({ theme }) => theme.spacing('1000')};
  gap: ${({ theme }) => theme.spacing('1000')};
  border-top-left-radius: ${({ theme }) => theme.shape.corner['3000']};
  border-bottom-right-radius: ${({ theme }) => theme.shape.corner['3000']};
  border-bottom-left-radius: ${({ theme }) => theme.shape.corner['3000']};
  background: ${({ theme }) => theme.color.surfaceContainer};

  ${({ theme }) => theme.breakpoints.down('lg')} {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    padding: ${({ theme }) => theme.spacing('0500')};
    gap: ${({ theme }) => theme.spacing('0500')};
    border-bottom-right-radius: ${({ theme }) => theme.shape.corner['2500']};
    border-bottom-left-radius: ${({ theme }) => theme.shape.corner['2500']};
  }
`;

const LoreTileContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  padding: ${({ theme }) => theme.spacing('1000')};
  gap: ${({ theme }) => theme.spacing('1000')};
  border-top-left-radius: ${({ theme }) => theme.shape.corner['3000']};
  border-bottom-right-radius: ${({ theme }) => theme.shape.corner['3000']};
  border-bottom-left-radius: ${({ theme }) => theme.shape.corner['3000']};
  background: ${({ theme }) => theme.color.surfaceContainer};

  ${({ theme }) => theme.breakpoints.down('lg')} {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    padding: ${({ theme }) => theme.spacing('0500')};
    gap: ${({ theme }) => theme.spacing('0500')};
    border-bottom-right-radius: ${({ theme }) => theme.shape.corner['2500']};
    border-bottom-left-radius: ${({ theme }) => theme.shape.corner['2500']};
  }
`;

const HolderTileContainer = styled('div', transientOptions)<{
  $showJourney: boolean;
}>`
  box-sizing: border-box;
  display: grid;
  align-self: stretch;
  padding: ${({ theme }) => theme.spacing('1000')};
  row-gap: ${({ theme }) => theme.spacing('1000')};
  column-gap: ${({ theme }) => theme.spacing('1000')};
  border-top-left-radius: ${({ theme }) => theme.shape.corner['3000']};
  border-bottom-right-radius: ${({ theme }) => theme.shape.corner['3000']};
  border-bottom-left-radius: ${({ theme }) => theme.shape.corner['3000']};
  background: ${({ theme }) => theme.color.surfaceContainer};

  grid-template-rows: ${({ $showJourney }) =>
    $showJourney
      ? 'minmax(0, 1fr) minmax(0, 1fr) 26.25rem 30rem 13.75rem'
      : 'minmax(0, 1fr) minmax(0, 1fr) 26.25rem 13.75rem'};
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (min-width: 1400px) {
    grid-template-rows: ${({ $showJourney }) =>
      $showJourney
        ? 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) 30rem minmax(0, 1fr)'
        : 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)'};
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  ${({ theme }) => theme.breakpoints.down('lg')} {
    display: flex;
    flex-direction: column;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    padding: ${({ theme }) => theme.spacing('0500')};
    gap: ${({ theme }) => theme.spacing('0500')};
  }
`;

const Row = styled.div`
  box-sizing: border-box;
  height: 30rem;
  display: grid;
  align-self: stretch;
  row-gap: ${({ theme }) => theme.spacing('1000')};
  column-gap: ${({ theme }) => theme.spacing('1000')};
  grid-template-rows: repeat(1, minmax(0, 1fr));
  grid-template-columns: repeat(3, minmax(0, 1fr));

  ${({ theme }) => theme.breakpoints.down('lg')} {
    all: unset;
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing('1000')};
    /* all:unset also drops the stretch, so each stacked row sized itself to
       its own content and the tiles came out at different widths. */
    align-self: stretch;
    width: 100%;
    box-sizing: border-box;
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    gap: ${({ theme }) => theme.spacing('0500')};
  }
`;

const SpanTwoContainer = styled.div`
  grid-column: 1 / span 2;
`;

const SpanOneContainer = styled.div`
  grid-column: 3 / span 1;
`;

const SpanOneLeftContainer = styled.div`
  grid-column: 1 / span 1;
`;

const SpanTwoRightContainer = styled.div`
  grid-column: 2 / span 2;
`;

const SpanThreeContainer = styled.div`
  grid-column: 1 / span 3;
`;

const FilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing('1000')};
  flex: 1 0 0;
  align-self: stretch;
  grid-row: 1 / span 3;
  grid-column: 1 / span 1;

  @media (max-width: 1399.95px) {
    grid-row: 1 / span 2;
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    gap: ${({ theme }) => theme.spacing('0500')};
  }
`;

const UserHubContainer = styled.div`
  height: auto;
  width: 100%;
`;

// PointsHubContainer / QuestsHubContainer removed during Mongo->Supabase migration.

const StakingHubContainer = styled.div`
  height: 100%;
  width: 100%;
`;

const ProgramHubContainer = styled.div`
  grid-row: 2 / span 2;
  grid-column: 2 / span 2;

  @media (max-width: 1399.95px) {
    grid-row: 3 / span 1;
    grid-column: 1 / span 2;
  }
`;

const JourneyHubContainer = styled.div`
  grid-row: 4 / span 1;
  grid-column: 1 / span 3;

  @media (max-width: 1399.95px) {
    grid-row: 4 / span 1;
    grid-column: 1 / span 2;
  }
`;

const BadgesHubContainer = styled.div`
  grid-row: -2 / -1;
  grid-column: 1 / span 3;

  @media (max-width: 1399.95px) {
    grid-column: 1 / span 2;
  }
`;

const ImageTrailContainer = styled.div`
  height: 100vh;
  position: absolute;
  top: 0;
  z-index: 0;
  /* Spans the whole viewport, not the padded content column: the trail is
     driven by this element's own rect, so a column-width box made the images
     stop dead at the left and right gutters. Still bounded vertically, so the
     effect ends where the tiles begin. */
  left: 50%;
  transform: translateX(-50%);
  width: 100vw;
  max-width: 100vw;
  overflow: hidden;
`;
type TabType = 'ecosystem' | 'lore' | 'products' | 'holder';

export default function HubPage() {
  const t = useTranslations();
  const router = useRouter();
  const searchParams = useSearchParams();

  const requestedTab = searchParams.get('tab') as TabType | null;
  const [activeTab, setActiveTab] = useState<TabType>(
    // A ?tab=holder link (or an old bookmark) must not land on a tab the
    // public build does not render.
    env.SHELL_MODE && requestedTab !== 'products'
      ? 'lore'
      : (requestedTab ?? 'ecosystem'),
  );

  const completedUser = useCurrentCompletedUser();

  const { isDarkMode } = useDesignsTokenTheme();

  const trailImages = useMemo(
    () => getRandomImages(isDarkMode ? DARK_IMAGES : LIGHT_IMAGES, 20),
    [isDarkMode],
  );

  const handleChangeTab = (tab: TabType) => {
    router.replace(`?tab=${tab}`, { scroll: false });
    setActiveTab(tab);
  };

  useEffect(() => {
    if (env.SHELL_MODE) return;
    if (completedUser) {
      router.replace(`?tab=holder`, { scroll: false });
      setActiveTab('holder');
    }
  }, []);

  const renderTabContent = () => {
    switch (activeTab) {
      // Unchanged, so the app with the flag off is exactly what it was.
      case 'ecosystem':
        return (
          <EcoSystemTileContainer>
            <Row>
              <SpanTwoContainer>
                <CollectionHubTile />
              </SpanTwoContainer>

              <SpanOneContainer>
                <UnaTokenTile />
              </SpanOneContainer>
            </Row>

            <Row>
              <SpanOneLeftContainer>
                <HanaAiAgentTile />
              </SpanOneLeftContainer>

              <SpanTwoRightContainer>
                <RecordsTile />
              </SpanTwoRightContainer>
            </Row>

            <Row>
              <SpanTwoContainer>
                <ImagineTile />
              </SpanTwoContainer>

              <SpanOneContainer>
                <BrandAssetsTile />
              </SpanOneContainer>
            </Row>
          </EcoSystemTileContainer>
        );

      case 'products':
        return (
          <EcoSystemTileContainer>
            <Row>
              <SpanOneLeftContainer>
                <UnaTokenTile />
              </SpanOneLeftContainer>

              <SpanTwoRightContainer>
                <RecordsTile />
              </SpanTwoRightContainer>
            </Row>

            <Row>
              <SpanTwoContainer>
                <ImagineTile />
              </SpanTwoContainer>

              <SpanOneContainer>
                <HanaAiAgentTile />
              </SpanOneContainer>
            </Row>

            <Row>
              <SpanThreeContainer>
                <TheRoomTile />
              </SpanThreeContainer>
            </Row>
          </EcoSystemTileContainer>
        );

      case 'lore':
        return (
          <LoreTileContainer>
            <Row>
              <SpanOneLeftContainer>
                <LoreTile />
              </SpanOneLeftContainer>
              <SpanTwoRightContainer>
                <MangaTile />
              </SpanTwoRightContainer>
            </Row>
            <Row>
              <SpanTwoContainer>
                <CollectionHubTile />
              </SpanTwoContainer>
              <SpanOneContainer>
                <CouncilTile />
              </SpanOneContainer>
            </Row>
            <Row>
              <SpanThreeContainer>
                <TrailersTile />
              </SpanThreeContainer>
            </Row>
          </LoreTileContainer>
        );

      case 'holder':
        return (
          <HolderTileContainer $showJourney={!completedUser}>
            <FilterContainer>
              <UserHubContainer>
                <UserHubTile />
              </UserHubContainer>
              <StakingHubContainer>
                <StakingHubTile />
              </StakingHubContainer>
            </FilterContainer>

            {/* PointsHubTile and QuestsHubTile removed during the
                Mongo->Supabase migration. Re-add when the new systems land. */}

            <ProgramHubContainer>
              <PersonaProgramTile />
            </ProgramHubContainer>

            {!completedUser && (
              <JourneyHubContainer>
                <PersonaJourneyTile />
              </JourneyHubContainer>
            )}

            <BadgesHubContainer>
              <BadgesHubTile />
            </BadgesHubContainer>
          </HolderTileContainer>
        );

      default:
        return null;
    }
  };

  return (
    <HomeLayout>
      <ImageTrailContainer>
        <ImageTrail
          key={isDarkMode ? 'dark' : 'light'}
          items={trailImages}
          variant={9}
        />
      </ImageTrailContainer>
      <HeroMessage>
        <HeroMessageTitle>{t('HUB_PAGE_HERO_TITLE')}</HeroMessageTitle>
        <HeroMessageSubtitle>
          {t('HUB_PAGE_HERO_DESCRIPTION')}
        </HeroMessageSubtitle>
      </HeroMessage>
      <Container>
        <Sidebar>
          {/* The Valley experience is still work in progress, so the public
              build does not advertise a way into it. */}
          <TimeFallValleyTile />
          <BrandAssetsTile />
          <UnaTokenChartTile />
          {/*
            CloutChaseTile and ChatTile intentionally not rendered here.
            The components remain in the codebase for future reuse — see
            features/hub-rework/CloutChaseTile and features/hub-rework/ChatTile.
          */}
        </Sidebar>

        <Content>
          <DynamicContent>
            <TabsContainer>
              {/* The public build leads with Lore — the collection, the
                  Council, the manga and the trailers — then Products. */}
              {!env.SHELL_MODE && (
                <Tab
                  active={activeTab === 'ecosystem'}
                  onClick={() => handleChangeTab('ecosystem')}
                >
                  <TabLabel>{t('HUB_ECOSYSTEM_TAB')}</TabLabel>
                </Tab>
              )}
              <Tab
                active={activeTab === 'lore'}
                onClick={() => handleChangeTab('lore')}
              >
                <TabLabel>{t('HUB_LORE_TAB')}</TabLabel>
              </Tab>
              {env.SHELL_MODE && (
                <Tab
                  active={activeTab === 'products'}
                  onClick={() => handleChangeTab('products')}
                >
                  <TabLabel>Products</TabLabel>
                </Tab>
              )}
              {/* The Holder tab is the account surface — profile, staking,
                  the Persona Program and badges. None of it exists without an
                  account layer, so the public build does not offer it. */}
              {!env.SHELL_MODE && (
                <Tab
                  active={activeTab === 'holder'}
                  onClick={() => handleChangeTab('holder')}
                >
                  <TabLabel>{t('HUB_HOLDER_TAB')}</TabLabel>

                  {completedUser ? (
                    <TabAvatar
                      src={completedUser.user.twitter.profilePictureURL}
                      alt={`${completedUser.user.twitter.displayName}'s profile picture`}
                      borderRadius="none"
                      tokenId={completedUser.user.twitter.personaPFPTokenId}
                    />
                  ) : (
                    <NotConnectedIcon size="1500" />
                  )}
                </Tab>
              )}
            </TabsContainer>
            {renderTabContent()}
          </DynamicContent>
        </Content>
      </Container>
    </HomeLayout>
  );
}
